import * as moment from 'moment'
import { Report } from './Report'
import { SerializedHistogram } from './SerializedHistogram'

export interface TracepointRule<DateType> {
    id: number
    user: string
    service: string
    environment: string
    transactionId: number
    transaction: string
    transactionTags: string[]
    annotations: Array<{ name: string; operator: string; value: string }>
    createdAt: DateType
    startAt: DateType | null
    activeUntil: DateType
    duration: number
    filterThresholdMs: number | null
    filterThresholdMemoryMb: number | null
    notifyOnFirst: boolean
    notifyOnFirstSlow: boolean
    notifyOnFirstSlowThreshold: number | null
    notifyOnCompletion: boolean
    notificationIntegrations: number[]
    enableCallgraph: boolean
    callgraphDuration: number
    completed: boolean
    editable: boolean
    notificationSentOnFirst: boolean
    notificationSentOnFirstSlow: boolean
    notificationSentOnCompletion: boolean
    report?: TracepointReport
    referenceId: string
    generatedBy: 'user' | 'automatic' | 'on_new_trial_service' | 'performance_report'
    traceReference: { aggregationId: string; aggregationResponseTime: number } | null
}

interface TracepointReport {
    transactionName: string
    applicationName: string
    organizationName: string
    applicationReport: Report<SerializedHistogram>
    traceCollectionReport: TraceCollectionReport
}

interface TraceCollectionReport {
    supportsCallgraphStats: boolean
    totalCount: number
    totalCountCallgraphs: number
    tracesSlice: TraceView[]
    callgraphsSlice: TraceView[]
}

interface TraceView {
    id: string
    wallTime: number
}

export const tracepointIntrospectionParams = (tracepoint: TracepointRule<moment.Moment>) => {
    const startAt = tracepointStartAt(tracepoint)
    const rangeInMinutes = Math.max(1, moment.duration(tracepoint.activeUntil.diff(startAt)).asMinutes())

    return {
        operation: tracepoint.transactionId,
        env: tracepoint.environment,
        s: tracepoint.service,
        m: 3 * rangeInMinutes,
        ts: tracepoint.activeUntil
            .clone()
            .add(rangeInMinutes, 'm')
            .format('YYYY-MM-DD HH:mm'),
        date: startAt.format('YYYY-MM-DD HH:mm:ss'),
        end: tracepoint.activeUntil.format('YYYY-MM-DD HH:mm:ss'),
        opId: [tracepoint.transactionId],
    }
}

export const tracepointStartAt = <DateType>(tracepoint: TracepointRule<DateType>): DateType => tracepoint.startAt ?? tracepoint.createdAt

export const isTracepointActive = (tracepoint: TracepointRule<moment.Moment>, now: moment.Moment = moment.utc()): boolean =>
    !tracepoint.completed && !tracepointStartAt(tracepoint).isAfter(now) && tracepoint.activeUntil.isAfter(now)
